import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// DB type (snake_case)
export type DbTariff = {
  id: string;
  user_id: string;
  name: string;
  tariff_type: string;
  pricing_model: 'per_hour' | 'per_day' | 'per_week' | 'per_month' | 'fixed';
  base_price: number;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

// Client type (camelCase)
export type Tariff = {
  id: string;
  userId: string;
  name: string;
  type: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';
  basePrice: number;
  description: string | null;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
};

// Mappers
export const dbTariffToTariff = (dbTariff: DbTariff): Tariff => ({
  id: dbTariff.id,
  userId: dbTariff.user_id,
  name: dbTariff.name,
  type: dbTariff.pricing_model === 'per_hour' ? 'hourly' : 
        dbTariff.pricing_model === 'per_day' ? 'daily' : 
        dbTariff.pricing_model === 'per_week' ? 'weekly' : 
        dbTariff.pricing_model === 'per_month' ? 'monthly' : 'custom',
  basePrice: dbTariff.base_price,
  description: dbTariff.description,
  isActive: dbTariff.is_active,
  createdAt: new Date(dbTariff.created_at),
  updatedAt: new Date(dbTariff.updated_at),
});

export const tariffToDbTariff = (tariff: Partial<Tariff>): Partial<DbTariff> => ({
  ...(tariff.name && { name: tariff.name }),
  ...(tariff.type && { 
    pricing_model: tariff.type === 'hourly' ? 'per_hour' : 
                  tariff.type === 'daily' ? 'per_day' : 
                  tariff.type === 'weekly' ? 'per_week' : 
                  tariff.type === 'monthly' ? 'per_month' : 'fixed',
    tariff_type: 'rental'
  }),
  ...(tariff.basePrice !== undefined && { base_price: tariff.basePrice }),
  ...(tariff.description !== undefined && { description: tariff.description }),
  ...(tariff.isActive !== undefined && { is_active: tariff.isActive }),
});

const TARIFFS_QUERY_KEY = 'tariffs';

type UseTariffsOptions = {
  notifyOnError?: boolean;
};

type UseTariffsResult = {
  tariffs: Tariff[];
  isLoading: boolean;
  error: Error | null;
  createTariff: (newTariff: Omit<Tariff, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => Promise<Tariff>;
  updateTariff: (updatedTariff: Partial<Tariff> & { id: string }) => Promise<Tariff>;
  deleteTariff: (tariffId: string) => Promise<void>;
};

export const useTariffsUnified = (options?: UseTariffsOptions): UseTariffsResult => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const notifyOnError = options?.notifyOnError ?? false;

  const getErrorMessage = (err: unknown, fallback = 'Operation failed'): string => {
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object' && 'message' in err && typeof (err as { message?: unknown }).message === 'string') {
      return (err as { message: string }).message;
    }
    return fallback;
  };

  const { data: tariffs = [], isLoading, error } = useQuery<Tariff[], Error>({
    queryKey: [TARIFFS_QUERY_KEY, user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data, error } = await supabaseWorker
        .from('tariffs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        if (notifyOnError) {
          toast.error('Не удалось загрузить тарифы.');
        }
        throw new Error(getErrorMessage(error, 'Не удалось загрузить тарифы'));
      }

      return (data as unknown as DbTariff[]).map(dbTariffToTariff);
    },
    enabled: !!user,
  });

  const createTariffMutation = useMutation({
    mutationFn: async (newTariff: Omit<Tariff, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<Tariff> => {
      if (!user) throw new Error('User not authenticated');

      const dbData: Omit<DbTariff, 'id' | 'created_at' | 'updated_at'> = {
        user_id: user.id,
        name: newTariff.name,
        tariff_type: 'rental',
        pricing_model: newTariff.type === 'hourly' ? 'per_hour' : 
                      newTariff.type === 'daily' ? 'per_day' : 
                      newTariff.type === 'weekly' ? 'per_week' : 
                      newTariff.type === 'monthly' ? 'per_month' : 'fixed',
        base_price: newTariff.basePrice,
        description: newTariff.description,
        is_active: newTariff.isActive,
      };

      const { data, error } = await supabaseWorker
        .from('tariffs')
        .insert(dbData)
        .select()
        .single();

      if (error) {
        toast.error('Не удалось создать тариф.');
        throw new Error(getErrorMessage(error, 'Не удалось создать тариф'));
      }

      return dbTariffToTariff(data as unknown as DbTariff);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TARIFFS_QUERY_KEY, user?.id] });
      toast.success('Тариф успешно создан.');
    },
  });

  const updateTariffMutation = useMutation({
    mutationFn: async (updatedTariff: Partial<Tariff> & { id: string }): Promise<Tariff> => {
      if (!user) throw new Error('User not authenticated');

      const dbData = tariffToDbTariff(updatedTariff);

      const { data, error } = await supabaseWorker
        .from('tariffs')
        .update(dbData)
        .eq('id', updatedTariff.id)
        .select()
        .single();

      if (error) {
        toast.error('Не удалось обновить тариф.');
        throw new Error(getErrorMessage(error, 'Не удалось обновить тариф'));
      }

      return dbTariffToTariff(data as unknown as DbTariff);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TARIFFS_QUERY_KEY, user?.id] });
      toast.success('Тариф успешно обновлен.');
    },
  });

  const deleteTariffMutation = useMutation({
    mutationFn: async (tariffId: string): Promise<void> => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabaseWorker.from('tariffs').delete().eq('id', tariffId);

      if (error) {
        toast.error('Не удалось удалить тариф.');
        throw new Error(getErrorMessage(error, 'Не удалось удалить тариф'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TARIFFS_QUERY_KEY, user?.id] });
      toast.success('Тариф успешно удален.');
    },
  });

  return {
    tariffs,
    isLoading,
    error,
    createTariff: createTariffMutation.mutateAsync,
    updateTariff: updateTariffMutation.mutateAsync,
    deleteTariff: deleteTariffMutation.mutateAsync,
  };
};
